import _get from 'lodash/get';
import Log from '../util/logger';
import PlayerFactory from './player-factory';
import MediaPlayerConfig from '../config/media-player-config';
import PluginManager from '../plugin/plugin-manager';
import ObservationCenter from '../comm/observation-center';
import MediaPlayerEvents from '../events/media-player-events';
import Messages from '../comm/message/messages';

/**
 *            .--.                  Try not.
 * ::\`--._,'.::.`._.--'/::     Do or do not.
 * ::::.  ` __::__ '  .::::    There is no try.
 * ::::::-:.`'..`'.:-::::::
 * ::::::::\ `--' /::::::::              -Yoda
 *
 */
/**
 *
 * @module MediaPlayer
 * @desc The base level object for Player access
 *
 */
class MediaPlayer {

    /**
     *
     * @constructor
     * @param {MediaPlayerConfig} config The MediaPlayerConfig for the player/
     *
     */
    constructor(config = new MediaPlayerConfig()) {

        /**
         *
         * @type {ObservationCenter}
         *
         */
        this.observationCenter = new ObservationCenter();

        /**
         *
         * @type {MediaPlayerConfig}
         * @desc Reference to the MediaPlayerConfig object used to create the player
         *
         */
        this.config = undefined;

        /**
         *
         * @type {PluginManager}
         *
         */
        this.pluginManager = undefined;

        /**
         *
         * @type {AbstractPlayer}
         * @desc This is a reference to the player created based on MediaPlayerConfig
         *
         */
        this.player = this.createPlayer(config);

        /**
         *
         * @type {MediaItem}
         * @desc The currently assigned MediaItem
         *
         */
        this.currentMediaItem = undefined;

    }

    /**
     *
     * @desc Sets the config property and creates the plugin manager
     * @param {MediaPlayerConfig} config
     * @todo clean up existing pluginManager?
     *
     */
    initConfig(config) {

        if(!(config instanceof MediaPlayerConfig)) {

            throw new Error('Config must be a MediaPlayerConfig object. For more information please see https://github.mlbam.net/pages/web-media/mlb-media-player/manual/examples.html');

        }

        this.currentMediaItem = _get(this.config, 'mediaItem');

        this.config = config;

        this.pluginManager = new PluginManager(this.config, this.observationCenter);

    }

    /**
     *
     * @desc Initializes the player & returns a Promise
     * @returns {Promise}
     *
     */
    initPlayer() {

        return this.player.initPlayer();

    }

    /**
     *
     * @param {MediaPlayerConfig} config (Optional) The MediaPlayerConfig object
     * @returns {AbstractPlayer} The player instance
     *
     */
    createPlayer(config) {

        if (config) {

            this.initConfig(config);

        }

        this.player = PlayerFactory.create(this.config, this.observationCenter);

        // Notify player created
        this.observationCenter.playerSubject.next(new Messages.BaseMessage(MediaPlayerEvents.MEDIA_PLAYER_CREATED, this));

        return this.player;

    }

    canPlayNewMediaItem(mediaItem) {

        const deviceInfo = _get(this.config, 'playerTypeConfig.deviceInfo');
        const srcPlayerType = PlayerFactory.getPlayerTypeFromSource(mediaItem.src, deviceInfo);
        const currentPlayerType = _get(this.player, 'playerTypeConfig.playerType');
        const canPlay = srcPlayerType === currentPlayerType;

        return canPlay;

    }

    /**
     *
     * @desc Resets the playerType config destroys the current player and creates
     * a new player to handle playback of the the provided MediaItem.
     * @param {MediaItem} mediaItem The new mediaItem
     * @returns {Promise} the `initPlayer()` promise
     * @private
     *
     */
    createNewPlayerForMediaItem(mediaItem) {

        // Remove the initial meidaItem is there was one
        this.config.playerTypeConfig.mediaItem = mediaItem;

        // Destroy the player
        this.player.destroy();

        this.player = undefined;

        // Create a new player, don't pass the config so the plugins aren't recreated
        this.createPlayer();

        // Init that player and then send the new media item
        return this.initPlayer()
            .catch((error) => {

                Log.error(error);

                return error;

            });

    }

    /**
     *
     * @desc Sets the source, vtt tracks and poster of the media to playback
     * @param {MediaItem} mediaItem The MediaItem representing the media to playback
     *
     */
    setMediaItem(mediaItem) {

        const currentSrc = _get(this.currentMediaItem, 'src');
        const newSrc = _get(mediaItem, 'src');

        if (currentSrc !== newSrc) {

            this.currentMediaItem = mediaItem;

            if (this.player && this.canPlayNewMediaItem(mediaItem)) {

                this.player.open(mediaItem);

                return Promise.resolve();

            } else if(this.player && !this.canPlayNewMediaItem(mediaItem)) {

                return this.createNewPlayerForMediaItem(mediaItem);

            }


        } else {

            Log.info('Already playing this mediaItem');
            Log.info('Current MediaItem', this.currentMediaItem);
            Log.info('New MediaItem', mediaItem);

            return Promise.resolve();

        }

    }


    /**
     *
     * @desc Set poster for video player
     * @param {string} imgUrl Image path/url
     * @memberof MediaPlayer
     *
     */
    setPoster(imgUrl) {

        this.config.poster = imgUrl;
        this.player.setPoster(imgUrl);

    }

    /**
     *
     * @desc Pause the media when in a playing state
     *
     */
    pause() {

        this.player.pause();

    }

    /**
     *
     * @desc Play the media from a paused or stopped state
     *
     */
    play() {

        this.player.play();

    }

    /**
     *
     * @desc Replay the media
     *
     */
    replay() {

        this.player.replay();
        this.observationCenter.playerSubject.next(new Messages.BaseMessage(MediaPlayerEvents.MEDIA_REPLAY, this));

    }

    /**
     *
     * @desc Seeks to a specific time
     * @param {Number} time the time in seconds to seek to
     *
     */
    seek(time) {

        this.player.seek(time);

    }

    /**
     *
     * @desc Mutes the player
     *
     */
    mute() {

        this.player.mute();

    }

    /**
     *
     * @desc Un-mutes the player
     *
     */
    unMute() {

        this.player.unMute();

    }

    /**
     *
     * @desc Seeks to a specific time
     * @param {Number} time the time in seconds to seek to
     *
     */
    jumpToLive(time) {

        this.player.jumpToLive(time);

    }

    /**
     *
     * @desc Sets the volume.
     * @param {number} volume a value between 0 and 1
     *
     */
    setVolume(volume) {

        this.player.setVolume(volume);

    }

    /**
     *
     * @desc Request fullscreen for the specified DOM element
     * @param {*} element The DOM element to target for fullscreen
     *
     */
    requestFullscreen(element) {

        this.player.requestFullscreen(element);

    }

    /**
     *
     * @desc Exit fullscreen
     *
     */
    exitFullscreen() {

        this.player.exitFullscreen();

    }

    /**
     *
     * @desc Sets isAdPlaying on the state and updates any observers
     * @param {boolean} value if an ad is playing or not
     *
     */
    setIsAdPlaying(value) {

        this.player.setIsAdPlaying(value);

    }

    /**
     *
     * @desc Remove default controls attr
     *
     */
    removeDefaultControls() {

        this.player.removeDefaultControls();

    }

    /**
     *
     * @desc {int} index indicate the player to display captions/subtitles
     *
     */
    showClosedCaptions(index) {

        this.player.showClosedCaptions(index);

    }

    /**
     *
     * @desc indicate the player to hide captions/subtitles
     *
     */
    hideClosedCaptions() {

        this.player.hideClosedCaptions();

    }

    /**
     *
     * @desc Getter for the Log util
     *
     */
    get Log() {

        return Log;

    }

}

export default MediaPlayer;
